import { userService } from '../../services/api/user.service';import React, { useState, useEffect, useCallback } from 'react';
import { 
  Table, Card, Button, Space, Input, Modal, Form, 
  Select, message, Tag, Popconfirm, Row, Col, 
  Tabs, Tooltip
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined,
  HistoryOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
  FilterOutlined,
  SearchOutlined,
  DownloadOutlined,
  SyncOutlined
} from '@ant-design/icons';
import { waterMeterService } from '../../services/api/water-meter.service';
import ReadingHistory from './ReadingHistory';
import MeterHealth from './MeterHealth';
import type { WaterMeter } from '../../types/waterMeter';
import dayjs from 'dayjs';

const { TabPane } = Tabs;
const { Search } = Input;

const WaterMeterManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [meters, setMeters] = useState<WaterMeter[]>([]);
  const [filteredMeters, setFilteredMeters] = useState<WaterMeter[]>([]);
  const [selectedMeter, setSelectedMeter] = useState<WaterMeter | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [healthModalVisible, setHealthModalVisible] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [filterModalVisible, setFilterModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [filterForm] = Form.useForm();

  useEffect(() => {
    fetchWaterMeters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    filterMeters();
  }, [searchText, meters, activeTab]);

  const fetchWaterMeters = async () => {
    try {
      setLoading(true);
      const response = await waterMeterService.getAllMeters();
      setMeters(response.data);
      setFilteredMeters(response.data);
    } catch (error) {
      message.error('Error fetching water meters');
    } finally {
      setLoading(false);
    }
  };

  const filterMeters = () => {
    let filtered = [...meters];
    
    // Apply search filter
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(meter => 
        meter.meter_id.toLowerCase().includes(searchLower) ||
        meter.owner_name.toLowerCase().includes(searchLower) ||
        meter.address.toLowerCase().includes(searchLower) ||
        meter.code?.toLowerCase().includes(searchLower)
      );
    }
    
    // Apply tab filter
    if (activeTab === 'active') {
      filtered = filtered.filter(meter => meter.is_active === '1');
    } else if (activeTab === 'inactive') {
      filtered = filtered.filter(meter => meter.is_active === '0');
    }
    
    setFilteredMeters(filtered);
  };

  const handleAdd = () => {
    setSelectedMeter(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (meter: WaterMeter) => {
    setSelectedMeter(meter);
    form.setFieldsValue({
      meter_id: meter.meter_id,
      code: meter.code,
      owner_name: meter.owner_name,
      address: meter.address,
      street_name: meter.street_name,
      location_code: meter.location_code,
      meter_type: meter.meter_type,
      is_active: meter.is_active === '1'
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await waterMeterService.deleteMeter(id);
      message.success('Water meter deleted successfully');
      fetchWaterMeters();
    } catch (error) {
      message.error('Error deleting water meter');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      // Convert boolean to string for API
      const formattedValues = {
        ...values,
        is_active: values.is_active ? '1' : '0'
      };
      
      if (selectedMeter) {
        await waterMeterService.updateMeter(selectedMeter.id, formattedValues);
        message.success('Water meter updated successfully');
      } else {
        await waterMeterService.createMeter(formattedValues);
        message.success('Water meter created successfully');
      }
      setModalVisible(false);
      fetchWaterMeters();
    } catch (error) {
      message.error('Error saving water meter');
    }
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
  };

  const handleFilter = async () => {
    try {
      const values = await filterForm.validateFields();
      let filtered = [...meters];
      
      if (values.meter_type) {
        filtered = filtered.filter(meter => meter.meter_type === values.meter_type);
      }
      
      if (values.status !== 'all') {
        filtered = filtered.filter(meter => meter.is_active === values.status);
      }
      
      if (values.location) {
        filtered = filtered.filter(meter => meter.address.includes(values.location));
      }
      
      setFilteredMeters(filtered);
      setFilterModalVisible(false);
    } catch (error) {
      console.error('Filter error:', error);
    }
  };

  const resetFilters = () => {
    filterForm.resetFields();
    setFilteredMeters(meters);
    setFilterModalVisible(false);
  };

  const exportMeters = () => {
    try {
      const csvContent = "data:text/csv;charset=utf-8," 
        + "ID,Meter ID,Code,Owner,Address,Type,Status\n"
        + filteredMeters.map(meter => [
            meter.id,
            meter.meter_id,
            meter.code || '',
            meter.owner_name,
            meter.address,
            meter.meter_type,
            meter.is_active === '1' ? 'Active' : 'Inactive'
          ].join(",")).join("\n");
      
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `water-meters-${dayjs().format('YYYY-MM-DD')}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      message.success('Export completed');
    } catch (error) {
      message.error('Error exporting data');
    }
  };

  // Get unique meter types from data for filters
  const uniqueMeterTypes = Array.from(new Set(meters.map(m => m.meter_type)))
    .filter(Boolean)
    .map(type => ({ text: type, value: type }));

  // Create properly typed columns
  const columns= [
    {
      title: 'Meter ID',
      dataIndex: 'meter_id',
      key: 'meter_id',
      width: 150,
    },
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      width: 100,
    },
    {
      title: 'Owner',
      dataIndex: 'owner_name',
      key: 'owner_name',
    },
    {
      title: 'Address',
      dataIndex: 'address',
      key: 'address',
    },
    {
      title: 'Type',
      dataIndex: 'meter_type',
      key: 'meter_type',
      width: 120,
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (is_active: string) => (
        <Tag color={is_active === '1' ? 'green' : 'red'}>
          {is_active === '1' ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'action',
      width: 150,
      render: (_: any, record: WaterMeter) => (
        <Space size="small">
          <Tooltip title="Meter Health Check">
            <Button 
              type="text" 
              icon={<WarningOutlined />} 
              onClick={() => {
                setSelectedMeter(record);
                setHealthModalVisible(true);
              }}
            />
          </Tooltip>
          <Tooltip title="Reading History">
            <Button 
              type="text" 
              icon={<HistoryOutlined />} 
              onClick={() => {
                setSelectedMeter(record);
                setHistoryModalVisible(true);
              }}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button 
              type="text" 
              icon={<EditOutlined />} 
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Popconfirm
              title="Are you sure you want to delete this meter?"
              onConfirm={() => handleDelete(record.id)}
              okText="Yes"
              cancelText="No"
              icon={<ExclamationCircleOutlined style={{ color: 'red' }} />}
            >
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="p-6">
      <Card>
        <div className="flex justify-between items-center mb-6">
          <Search
            placeholder="Search water meters..."
            onSearch={handleSearch}
            style={{ width: 300 }}
            allowClear
          />
          <Space>
            <Button 
              icon={<FilterOutlined />} 
              onClick={() => setFilterModalVisible(true)}
            >
              Filters
            </Button>
            <Button 
              icon={<DownloadOutlined />} 
              onClick={exportMeters}
            >
              Export
            </Button>
            <Button 
              icon={<SyncOutlined />} 
              onClick={fetchWaterMeters}
            >
              Refresh
            </Button>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={handleAdd}
            >
              New Water Meter
            </Button>
          </Space>
        </div>

        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="All Meters" key="all" />
          <TabPane tab="Active" key="active" />
          <TabPane tab="Inactive" key="inactive" />
        </Tabs>

        <Table
          columns={columns}
          dataSource={filteredMeters}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} water meters`
          }}
        />
      </Card>

      {/* Water Meter Form Modal */}
      <Modal
        title={selectedMeter ? 'Edit Water Meter' : 'New Water Meter'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="meter_id"
                label="Meter ID"
                rules={[{ required: true, message: 'Required field' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="code"
                label="Code"
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="owner_name"
            label="Owner"
            rules={[{ required: true, message: 'Required field' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="address"
            label="Address"
            rules={[{ required: true, message: 'Required field' }]}
          >
            <Input />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="street_name"
                label="Street"
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="location_code"
                label="Location Code"
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="meter_type"
                label="Meter Type"
              >
                <Select>
                  <Select.Option value="MBUS 3/4">MBUS 3/4</Select.Option>
                  <Select.Option value="MBUS 1/2">MBUS 1/2</Select.Option>
                  <Select.Option value="Standard">Standard</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="is_active"
                label="Status"
                valuePropName="checked"
              >
                <Select>
                  <Select.Option value={true}>Active</Select.Option>
                  <Select.Option value={false}>Inactive</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Filter Modal */}
      <Modal
        title="Filter Water Meters"
        open={filterModalVisible}
        onOk={handleFilter}
        onCancel={() => setFilterModalVisible(false)}
        footer={[
          <Button key="reset" onClick={resetFilters}>
            Reset
          </Button>,
          <Button key="cancel" onClick={() => setFilterModalVisible(false)}>
            Cancel
          </Button>,
          <Button key="apply" type="primary" onClick={handleFilter}>
            Apply Filters
          </Button>,
        ]}
      >
        <Form
          form={filterForm}
          layout="vertical"
        >
          <Form.Item name="meter_type" label="Meter Type">
            <Select allowClear placeholder="Select meter type">
              {uniqueMeterTypes.map(option => (
                <Select.Option key={option.value} value={option.value}>
                  {option.text}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          
          <Form.Item name="status" label="Status" initialValue="all">
            <Select>
              <Select.Option value="all">All</Select.Option>
              <Select.Option value="1">Active</Select.Option>
              <Select.Option value="0">Inactive</Select.Option>
            </Select>
          </Form.Item>
          
          <Form.Item name="location" label="Location">
            <Input placeholder="Filter by location" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Reading History Modal */}
      <Modal
        title={`Reading History - ${selectedMeter?.meter_id}`}
        open={historyModalVisible}
        onCancel={() => setHistoryModalVisible(false)}
        width={800}
        footer={null}
      >
        {selectedMeter && (
          <ReadingHistory meterId={selectedMeter.id} />
        )}
      </Modal>

      {/* Meter Health Modal */}
      <Modal
        title={`Meter Health Check - ${selectedMeter?.meter_id}`}
        open={healthModalVisible}
        onCancel={() => setHealthModalVisible(false)}
        width={600}
        footer={null}
      >
        {selectedMeter && (
          <MeterHealth meterId={selectedMeter.id} />
        )}
      </Modal>
    </div>
  );
};

export default WaterMeterManagement;